import React, { useContext, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Building2, Check, Loader2, User, Sun, Moon, ChevronDown } from "lucide-react";
import { useAppStore } from "../../store";
import { useSecurityContext } from "../../contexts/SecurityContext";
import { useTenantContext } from "../../contexts/TenantContext";
import { cn } from "../../utils";
import { getRouteConfig } from "../../config/routes";
import { Button } from "../ui/button";
import { hasActivePageHeaderRegistration, usePageHeaderActions, usePageHeaderMeta } from "../../lib/workspace/page-header";
import { APP_SHELL } from "../../lib/constants/workspace-layout";
import { PageContext } from "./PageContext";
import { SearchableSelect } from "@/components/workspace/SearchableSelect";
import { ListToolbarSelect } from "@/components/workspace/ListToolbarSelect";
import { Popover } from "../../lib/radix";
import { Command } from "../../lib/cmdk";
import type { GovernanceAuthzTenant } from "@/lib/api/api-types";
import { useTranslation } from "@/lib/hooks/use-translation";
import { resolvePathChromeTitle } from "@/lib/i18n/localize-navigation";
import { LanguageSwitcher } from "./language-switcher";
import { LogoutConfirmDialog } from "./LogoutConfirmDialog";
import { HeaderAccountMenu } from "./HeaderAccountMenu";

type OrganizationSwitcherTreeNode = {
  tenant: GovernanceAuthzTenant;
  children: OrganizationSwitcherTreeNode[];
};

type OrganizationSwitcherFlatRow = {
  tenant: GovernanceAuthzTenant;
  depth: number;
};

function tenantLabel(tenant: GovernanceAuthzTenant | null, fallbackTenantId: string): string {
  const name = String(tenant?.name ?? "").trim();
  if (name) {
    return name;
  }
  const tenantId = String(tenant?.tenant_id ?? "").trim();
  if (tenantId) {
    return tenantId;
  }
  return fallbackTenantId;
}

function getTenantParentId(tenant: GovernanceAuthzTenant): string {
  return String(tenant.parent_tenant_id ?? "").trim();
}

function compareOrganizationSwitcherTenants(left: GovernanceAuthzTenant, right: GovernanceAuthzTenant): number {
  const leftId = String(left.tenant_id ?? "").trim();
  const rightId = String(right.tenant_id ?? "").trim();
  const byLabel = tenantLabel(left, leftId).localeCompare(tenantLabel(right, rightId));
  if (byLabel !== 0) {
    return byLabel;
  }
  return leftId.localeCompare(rightId);
}

function buildOrganizationSwitcherForest(tenants: GovernanceAuthzTenant[]): OrganizationSwitcherTreeNode[] {
  const byId = new Map<string, GovernanceAuthzTenant>();
  for (const tenant of tenants) {
    const tenantId = String(tenant.tenant_id ?? "").trim();
    if (!tenantId) {
      continue;
    }
    byId.set(tenantId, tenant);
  }
  const childrenByParent = new Map<string, GovernanceAuthzTenant[]>();
  const roots: GovernanceAuthzTenant[] = [];
  for (const tenant of byId.values()) {
    const tenantId = String(tenant.tenant_id).trim();
    const parentId = getTenantParentId(tenant);
    if (parentId && byId.has(parentId) && parentId !== tenantId) {
      const siblings = childrenByParent.get(parentId);
      if (siblings) {
        siblings.push(tenant);
      } else {
        childrenByParent.set(parentId, [tenant]);
      }
      continue;
    }
    roots.push(tenant);
  }
  const buildNode = (tenant: GovernanceAuthzTenant): OrganizationSwitcherTreeNode => {
    const tenantId = String(tenant.tenant_id).trim();
    const childTenants = childrenByParent.get(tenantId) ?? [];
    childTenants.sort(compareOrganizationSwitcherTenants);
    return {
      tenant,
      children: childTenants.map(buildNode),
    };
  };
  roots.sort(compareOrganizationSwitcherTenants);
  return roots.map(buildNode);
}

function flattenOrganizationSwitcherForest(roots: OrganizationSwitcherTreeNode[]): OrganizationSwitcherFlatRow[] {
  const rows: OrganizationSwitcherFlatRow[] = [];
  const walk = (nodes: OrganizationSwitcherTreeNode[], depth: number) => {
    for (const node of nodes) {
      rows.push({ tenant: node.tenant, depth });
      walk(node.children, depth + 1);
    }
  };
  walk(roots, 0);
  return rows;
}

function tenantSearchText(tenant: GovernanceAuthzTenant): string {
  return [tenant.name, tenant.tenant_id]
    .map((item) => String(item ?? "").trim().toLowerCase())
    .filter((item) => item.length > 0)
    .join(" ");
}

function filteredTenants(tenants: GovernanceAuthzTenant[], query: string): GovernanceAuthzTenant[] {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return tenants;
  }
  return tenants.filter((tenant) => tenantSearchText(tenant).includes(normalizedQuery));
}

const OrganizationSwitcher: React.FC = () => {
  const { t } = useTranslation();
  const {
    activeTenant,
    activeTenantId,
    tenants,
    canSwitch,
    isLoading,
    isSwitching,
    error,
    switchActiveTenant,
  } = useTenantContext();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  // Enter-list: foothold orgs + current active only (see ≠ enter; catalog stays on Platform IAM).
  const enterableTenants = tenants.filter((tenant) => {
    const tenantId = String(tenant.tenant_id ?? "").trim();
    if (!tenantId) return false;
    if (tenantId === activeTenantId) return true;
    return tenant.switchable === true;
  });
  const visibleTenants = filteredTenants(enterableTenants, query);
  const switcherRows = flattenOrganizationSwitcherForest(buildOrganizationSwitcherForest(visibleTenants));
  const label = tenantLabel(activeTenant, activeTenantId);

  const handleSelect = async (tenantId: string) => {
    const selectedTenantId = tenantId.trim();
    if (!selectedTenantId) {
      return;
    }
    if (selectedTenantId === activeTenantId) {
      setOpen(false);
      return;
    }
    const switched = await switchActiveTenant(selectedTenantId);
    if (switched) {
      setOpen(false);
      setQuery("");
    }
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={cn(
            APP_SHELL.userPill,
            "max-w-[14rem] text-left hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring sm:max-w-[16rem]",
          )}
          aria-label={t("header.organizationSwitcher")}
          aria-expanded={open}
          aria-haspopup="dialog"
          data-testid="global-organization-switcher-trigger"
        >
          <Building2 className={APP_SHELL.userPillIcon} aria-hidden />
          <span
            className={cn(APP_SHELL.userPillText, "min-w-0 max-w-[9rem] truncate sm:max-w-[11rem]")}
            title={isLoading && !activeTenant ? t("header.organizationLoading") : label}
          >
            {isLoading && !activeTenant ? t("header.organizationLoading") : label}
          </span>
          {isSwitching ? (
            <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" aria-hidden />
          ) : (
            <ChevronDown
              className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform", open && "rotate-180")}
              aria-hidden
            />
          )}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={8}
          className="w-[min(24rem,calc(100vw-1rem))] rounded-xl border border-border surface-card p-2 shadow-2xl dark:shadow-black/40"
          data-testid="global-organization-switcher-content"
        >
          <Command className="space-y-2">
            <div className="rounded-lg border border-border bg-muted/40 p-3">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 shrink-0 text-primary" />
                <p className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">{label}</p>
              </div>
            </div>
            <Command.Input
              value={query}
              onValueChange={setQuery}
              placeholder={t("header.searchOrganization")}
              className="h-9 w-full rounded-lg border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
            />
            <Command.List className="max-h-72 overflow-auto pr-1">
              {switcherRows.length === 0 ? (
                <Command.Empty className="px-3 py-3 text-center text-xs text-muted-foreground">{t("header.noOrganizations")}</Command.Empty>
              ) : null}
              {switcherRows.map(({ tenant, depth }) => {
                const tenantId = String(tenant.tenant_id).trim();
                const selected = tenantId === activeTenantId;
                const switchable = tenant.switchable === true;
                return (
                  <Command.Item
                    key={tenantId}
                    value={tenantId}
                    disabled={!selected && !switchable}
                    onSelect={handleSelect}
                    style={{ paddingLeft: `${0.75 + depth * 0.75}rem` }}
                    className={cn(
                      "mb-1 flex cursor-pointer items-center gap-2 rounded-lg py-2 pr-3 text-sm outline-none hover:bg-muted focus:bg-muted",
                      !selected && !switchable && "cursor-not-allowed opacity-50",
                    )}
                  >
                    <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-border">
                      {selected ? <Check className="h-3.5 w-3.5 text-primary" /> : null}
                    </div>
                    <span className="min-w-0 flex-1 truncate font-medium text-foreground">
                      {tenantLabel(tenant, tenantId)}
                    </span>
                  </Command.Item>
                );
              })}
            </Command.List>
            {!canSwitch ? (
              <p className="px-1 text-xs text-muted-foreground">
                {t("header.noSwitchableOrganization")} {t("header.joinBeforeEnter")}
              </p>
            ) : null}
            {error ? <p className="rounded-lg bg-destructive/10 px-2 py-1 text-xs text-destructive">{error}</p> : null}
          </Command>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

interface HeaderProps {
  onToggleSidebar: () => void;
  sidebarCollapsed: boolean;
  onToggleMobileMenu: () => void;
  mobileMenuOpen: boolean;
}

export const Header: React.FC<HeaderProps> = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const theme = useAppStore((s) => s.theme);
  const toggleTheme = useAppStore((s) => s.toggleTheme);
  const { state: securityState, logout } = useSecurityContext();
  const { activeTenant } = useTenantContext();
  const membershipRoleLabel = Array.isArray(activeTenant?.role_ids)
    ? activeTenant.role_ids.map((roleId) => String(roleId).trim()).filter(Boolean).join(", ")
    : "";
  const routeMeta = getRouteConfig(location.pathname);
  const pageHeaderActions = usePageHeaderActions();
  const pageHeaderMeta = usePageHeaderMeta();
  const usesWorkspacePageHeader = hasActivePageHeaderRegistration();
  const pageContext = useContext(PageContext);
  const pageConfig = pageContext?.config;

  const registeredTitle = usesWorkspacePageHeader ? pageHeaderMeta?.title : undefined;
  const rawPageTitle = registeredTitle ?? pageConfig?.title ?? routeMeta.title;
  const pageTitle = registeredTitle
    ? registeredTitle
    : resolvePathChromeTitle(location.pathname, t, rawPageTitle);
  const accountLabel = String(securityState.userName || securityState.userRole || "").trim();
  const organizationLabel = tenantLabel(activeTenant, String(activeTenant?.tenant_id ?? "").trim());
  const rawPageHeaderActions = usesWorkspacePageHeader ? pageHeaderActions : pageConfig?.actions ?? [];
  const visiblePageHeaderActions = rawPageHeaderActions.filter((action) => {
    const label = action.label.trim().toLowerCase();
    if (!label) return true;
    if (label === "refresh" || label === "làm mới" || label === "tải lại") return false;
    if (label.startsWith("refresh ") || label.startsWith("làm mới") || label.startsWith("tải lại")) return false;
    return true;
  });
  const breadcrumbs = usesWorkspacePageHeader
    ? pageHeaderMeta?.breadcrumbs ?? routeMeta.breadcrumbs
    : pageConfig?.breadcrumbs && pageConfig.breadcrumbs.length > 0
      ? pageConfig.breadcrumbs
      : routeMeta.breadcrumbs;
  const visibleBreadcrumbs = breadcrumbs.length > 1 ? breadcrumbs : [];
  const headerIconButtonClassName = cn(
    "flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-ring focus-visible:shadow-ring-glow",
    APP_SHELL.iconButtonMuted
  );

  return (
    <header className={cn(APP_SHELL.headerBar, "shrink-0")}>
      <div className={APP_SHELL.headerInner}>
        <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
          <div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5">
            {visibleBreadcrumbs.length > 0 ? (
              <nav className="hidden min-w-0 text-muted-foreground sm:block" aria-label={t("header.breadcrumb")}>
                <ol className="flex max-w-full list-none flex-wrap items-center gap-x-0.5 gap-y-0.5 p-0 text-[10px] leading-tight sm:text-[11px]">
                  {visibleBreadcrumbs.map((crumb, index) => {
                    const current = index === visibleBreadcrumbs.length - 1;
                    const crumbLabel =
                      crumb.path === "/" || crumb.label === "Home" || crumb.label === "Trang chủ"
                        ? t("route.breadcrumb.home")
                        : resolvePathChromeTitle(crumb.path, t, crumb.label);
                    return (
                      <li key={`${crumb.path}-${index}`} className="flex min-w-0 shrink-0 items-center gap-x-0.5">
                        {index > 0 ? <span className="mx-0.5 select-none text-muted-foreground" aria-hidden>/</span> : null}
                        {current ? (
                          <span className="max-w-[min(100vw,18rem)] truncate font-semibold text-foreground" aria-current="page">
                            {crumbLabel}
                          </span>
                        ) : (
                          <Link
                            to={crumb.path}
                            className="max-w-[min(100vw,14rem)] truncate rounded-md px-1 py-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                          >
                            {crumbLabel}
                          </Link>
                        )}
                      </li>
                    );
                  })}
                </ol>
              </nav>
            ) : null}
            <div className="min-w-0">
              <h1 className={APP_SHELL.headerTitle}>{pageTitle}</h1>
            </div>
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap items-center justify-end gap-1 sm:gap-2">
          {visiblePageHeaderActions.length > 0 ? (
            <div className="hidden max-w-[42rem] flex-wrap items-center justify-end gap-2 xl:flex">
              {visiblePageHeaderActions.map((action) => {
                const hasIcon = Boolean(action.icon);
                return (
                  <Button
                    key={action.dataTestId ?? action.label}
                    variant={action.variant}
                    size="sm"
                    className={cn("rounded-lg", hasIcon ? "h-8 w-8 shrink-0 px-0" : null, action.className)}
                    onClick={action.onClick}
                    disabled={action.disabled}
                    data-testid={action.dataTestId}
                    aria-label={hasIcon ? action.label : undefined}
                    title={hasIcon ? action.label : undefined}
                  >
                    {action.icon}
                    {hasIcon ? null : <span>{action.label}</span>}
                  </Button>
                );
              })}
            </div>
          ) : null}
          {!usesWorkspacePageHeader && pageConfig?.toolbar ? <div className="hidden min-w-0 items-center xl:flex">{pageConfig.toolbar}</div> : null}
          {!usesWorkspacePageHeader && pageConfig?.timeRange ? (
            <ListToolbarSelect
              id="page-time-range-select"
              value={pageConfig.timeRange.value}
              onChange={(next) => pageConfig.timeRange?.onChange(next)}
              options={pageConfig.timeRange.options.map((option) => ({
                value: option.value,
                label: option.label,
              }))}
              placeholder={t("header.timeRange")}
              ariaLabel={t("header.timeRange")}
              testId="page-time-range-select"
              allowClear={false}
              className="hidden max-w-[180px] xl:block"
            />
          ) : null}
          <OrganizationSwitcher />
          <LanguageSwitcher />
          <button
            onClick={toggleTheme}
            className={headerIconButtonClassName}
            aria-label={theme === "dark" ? t("header.lightMode") : t("header.darkMode")}
            title={theme === "dark" ? t("header.lightMode") : t("header.darkMode")}
          >
            {theme === "dark" ? (
              <Sun className="h-5 w-5 transition-transform duration-300 hover:rotate-45 hover:scale-110" />
            ) : (
              <Moon className="h-5 w-5 transition-transform duration-300 hover:-rotate-12 hover:scale-110" />
            )}
          </button>

          {[!securityState.isAuthenticated, securityState.userRole === "guest"].includes(true) ? (
            <Link
              to="/login"
              className="flex h-10 items-center gap-2 rounded-lg bg-muted px-2.5 py-1.5 text-sm font-medium text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
              aria-label={t("header.signIn")}
            >
              <User className={APP_SHELL.userPillIcon} />
              <span>{t("header.signIn")}</span>
            </Link>
          ) : (
            <HeaderAccountMenu
              displayName={accountLabel}
              membershipRoleLabel={membershipRoleLabel || String(securityState.userRole ?? "").trim()}
              organizationLabel={organizationLabel}
              onSignOut={() => setShowLogoutConfirm(true)}
            />
          )}
        </div>
      </div>

      <LogoutConfirmDialog
        open={showLogoutConfirm}
        onOpenChange={setShowLogoutConfirm}
        session={{
          accountLabel,
          organizationLabel,
          roleLabel: membershipRoleLabel || String(securityState.userRole ?? "").trim(),
        }}
        onConfirm={() => {
          setShowLogoutConfirm(false);
          logout();
        }}
      />
    </header>
  );
};

export default Header;
